import { createInvalidatingMutationHookFrom, useGetProjectId } from '@/utils/hooks';
import {
    getOptionPresetGroups,
    updateOptionPresetGroup,
    publishOptionPresetGroup,
    unpublishOptionPresetGroup,
    createOptionPresetGroup,
    getOptionPresetGroupsWithItems,
    getOptionPresetMatrix,
    saveOptionPresetMatrix,
} from './option-preset-group.api';
import { deleteOptionPresetGroup } from './option-preset-group.api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { optionPresetGroupKeys } from './option-preset-group.queryKeys';
import { Project } from '@/types';
import { PresetItemsChangeSet } from './option-preset-group.types';

export const useOptionPresetGroups = (projectId?: Project['id']) => {
    return useQuery({
        queryKey: projectId ? optionPresetGroupKeys.list(projectId) : optionPresetGroupKeys.root,
        queryFn: () => getOptionPresetGroups(projectId!),
        enabled: !!projectId,
    });
};

export const useOptionPresetGroupsWithItems = (projectId?: Project['id']) => {
    return useQuery({
        queryKey: projectId ? optionPresetGroupKeys.withItems(projectId) : optionPresetGroupKeys.root,
        queryFn: () => getOptionPresetGroupsWithItems(projectId!),
        enabled: !!projectId,
    });
};

export const useOptionPresetGroupDelete = (projectId: number) => {
    return createInvalidatingMutationHookFrom(deleteOptionPresetGroup, optionPresetGroupKeys.list(projectId));
};

export const useOptionPresetGroupUpdate = () => {
    const projectId = useGetProjectId()!;
    return createInvalidatingMutationHookFrom(updateOptionPresetGroup, optionPresetGroupKeys.list(projectId));
};

export const useOptionPresetGroupCreate = () => {
    const projectId = useGetProjectId()!;
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createOptionPresetGroup,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionPresetGroupKeys.list(projectId) });
        },
    });
};

export const useOptionPresetGroupPublish = () => {
    const projectId = useGetProjectId()!;
    return createInvalidatingMutationHookFrom(publishOptionPresetGroup, optionPresetGroupKeys.list(projectId));
};

export const useOptionPresetGroupUnpublish = () => {
    const projectId = useGetProjectId()!;
    return createInvalidatingMutationHookFrom(unpublishOptionPresetGroup, optionPresetGroupKeys.list(projectId));
};

// Combined mutations for option preset groups to avoid importing multiple hooks at call sites
// Usage:
// const m = useOptionGroupMutations(group.projectId);
// m.delete([groupId]);
// await m.publishAsync([groupId]);
export const usePresetGroupMutations = (presetGroupId: number) => {
    const queryClient = useQueryClient();
    const projectId = useGetProjectId()!;
    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey: optionPresetGroupKeys.list(projectId) });
        queryClient.invalidateQueries({ queryKey: optionPresetGroupKeys.withItems(projectId) });
    };
    const deleteMutation = useMutation({
        mutationFn: () => deleteOptionPresetGroup(presetGroupId),
        onSuccess: onSuccess,
    });
    const publishMutation = useMutation({
        mutationFn: () => publishOptionPresetGroup(presetGroupId),
        onSuccess: onSuccess,
    });
    const unpublishMutation = useMutation({
        mutationFn: () => unpublishOptionPresetGroup(presetGroupId),
        onSuccess: onSuccess,
    });

    return {
        delete: () => deleteMutation.mutate(),
        deleteAsync: () => deleteMutation.mutateAsync(),
        publish: () => publishMutation.mutate(),
        publishAsync: () => publishMutation.mutateAsync(),
        unpublish: () => unpublishMutation.mutate(),
        unpublishAsync: () => unpublishMutation.mutateAsync(),
    };
};

// Loads the entire style/preset matrix in one request. Kept fresh during the editing session and not
// refetched on window focus; the explicit Save invalidates it.
export const useOptionPresetMatrix = (presetGroupId?: number) => {
    return useQuery({
        queryKey: optionPresetGroupKeys.matrix(presetGroupId!),
        queryFn: () => getOptionPresetMatrix(presetGroupId!),
        enabled: !!presetGroupId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

// Atomic save of all matrix changes; refreshes the matrix once on success.
export const useSaveOptionPresetMatrix = (presetGroupId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (changes: PresetItemsChangeSet[]) => saveOptionPresetMatrix(presetGroupId, changes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: optionPresetGroupKeys.matrix(presetGroupId) });
        },
    });
};
